import axios, { AxiosResponse, CancelTokenSource } from "axios"
import { AmortizacionDtoProps } from "./DTOs/AmortizacionDto"
import { AmortizacionReadingDtoProps } from "./DTOs/AmortizacionReadingDto"
import { PrestamoDtoProps } from "./DTOs/PrestamoDto"
import { PrestamoReadingDtoProps } from "./DTOs/PrestamoReadingDto"
import { adapterPrestamoById } from "./Prestamo.adapter"
import {
  adapterAmortizacionesPrestamo,
  getPrestamoByFiltersDtoAdapter,
} from "./Prestamos.adapter"
import { EntidadConciliableReadingDtoIPagedEnumerableProps } from "./Schemas/EntidadConciliableReadingDtoIPagedEnumerable"
import { GetPrestamoByFiltersDtoIPagedEnumerable } from "./Schemas/GetPrestamoByFiltersDtoIPagedEnumerable"
import { EstadoPrestamoType } from "./Types/EstadoPrestamo"
import { TipoPrestamoType } from "./Types/TipoPrestamo"
import { entidadConciliableReadingDtoAdapter } from "./adapter_globals/EntidadConciliableReadingDto.adapter"
import { CamposPersonalizadosProps } from "./interfaces/CamposPersonalizados"
import { ConciliablesQueryParamsProps } from "./interfaces/ConciliablesQueryParams"
import { SortingCriteria } from "./interfaces/SortingCriteria"

interface DeleteAmortizacionPrestamoParams {
  PrestamoId: string
  AmortizacionId: string
}

export async function getDownloadPrestamoPdf(queryParams: string) {
  const { data } = await axios.get(`/Prestamo/${queryParams}/documento`, {
    responseType: "blob",
  })

  return data
}

export async function deletePrestamo(id: string): Promise<AxiosResponse> {
  const resp = await axios.delete(`/Prestamo/${id}`)
  return resp
}

export async function deleteAmortizacionPrestamo({
  PrestamoId,
  AmortizacionId,
}: DeleteAmortizacionPrestamoParams): Promise<AxiosResponse> {
  const resp = await axios.delete(
    `/Prestamo/${PrestamoId}/amortizacion/${AmortizacionId}`
  )
  return resp
}

export async function getPrestamoById(
  id: string
): Promise<PrestamoReadingDtoProps> {
  const { data }: { data: PrestamoReadingDtoProps } = await axios.get(
    `/Prestamo/${id}`
  )
  return adapterPrestamoById(data)
}

export interface GetPrestamosByFiltersParamsProps {
  CamposPersonalizados?: Array<CamposPersonalizadosProps>
  CreationUserIds?: Array<string>
  Estado?: Array<EstadoPrestamoType>
  FechaLowerLimit?: string
  FechaUpperLimit?: string
  ImporteAmortizacionLowerLimit?: number
  ImporteAmortizacionUpperLimit?: number
  ImporteLowerLimit?: number
  ImporteUpperLimit?: number
  Keyword?: string
  NoTieneDeuda?: boolean
  PendienteDePago?: boolean
  TieneDeuda?: boolean
  TipoPrestamo: TipoPrestamoType
  SortingCriteria?: SortingCriteria
  IsEnabled?: boolean
  MaxResultCount?: number
  SkipCount?: number
}

// eslint-disable-next-line import/no-mutable-exports
export let prestamoByFiltersCall: CancelTokenSource
export async function getPrestamoByFilters(
  queryParams: GetPrestamosByFiltersParamsProps
): Promise<GetPrestamoByFiltersDtoIPagedEnumerable> {
  if (typeof prestamoByFiltersCall !== typeof undefined) {
    prestamoByFiltersCall.cancel("Previous Request Cancelled")
  }
  prestamoByFiltersCall = axios.CancelToken.source()
  const { data } = await axios.get("/Prestamo/by-filters/", {
    params: queryParams,
    cancelToken: prestamoByFiltersCall.token,
  })
  return {
    currentPage: data.currentPage,
    items: data.items?.map(getPrestamoByFiltersDtoAdapter),
    totalCount: data.totalCount,
    totales: data.totales,
  }
}

export interface GetAmortizacionesPrestamoByIdParamsProps {
  FechaLowerLimit?: string
  FechaUpperLimit?: string
}

// eslint-disable-next-line import/no-mutable-exports
export let amortizacionesPrestamosByIdCall: CancelTokenSource
export async function getAmortizacionesPrestamosById(
  prestamoId: string,
  queryParams: GetAmortizacionesPrestamoByIdParamsProps
): Promise<AmortizacionReadingDtoProps[]> {
  if (typeof amortizacionesPrestamosByIdCall !== typeof undefined) {
    amortizacionesPrestamosByIdCall.cancel("Previous Request Cancelled")
  }
  amortizacionesPrestamosByIdCall = axios.CancelToken.source()
  const { data } = await axios.get(
    `/Prestamo/${prestamoId}/amortizaciones-by-filters/`,
    { params: queryParams, cancelToken: amortizacionesPrestamosByIdCall.token }
  )
  return data.map((amortizacion: AmortizacionReadingDtoProps) =>
    adapterAmortizacionesPrestamo(amortizacion)
  )
}

export async function editPrestamo({
  data,
  id,
}: {
  data: PrestamoDtoProps
  id: string
}) {
  const resp = axios.put(`/prestamo/${id}`, data)
  return resp
}

export async function editAmortizaciones({
  prestamoId,
  amortizacionId,
  asientoContableStatus,
}: {
  prestamoId: string
  amortizacionId: string
  asientoContableStatus: boolean
}) {
  const resp = axios.put(
    `/prestamo/${prestamoId}/amortizacion/${amortizacionId}/contabilizar/${asientoContableStatus}`
  )
  return resp
}

export async function editAnotationPrestamo({
  prestamoId,
  amortizacionId,
  body,
}: {
  prestamoId: string
  amortizacionId: string
  body: string
}) {
  const resp = await axios.put(
    `/prestamo/${prestamoId}/amortizacion/${amortizacionId}/nota`,
    body,
    {
      headers: {
        "content-type": "application/json",
      },
    }
  )
  return resp
}

export async function putAmortizaciones({
  data,
  prestamoId,
}: {
  data: AmortizacionDtoProps
  prestamoId: string
}) {
  const resp = await axios.put(
    `prestamo/${prestamoId}/amortizacion-anticipada`,
    data
  )
  return resp
}

// eslint-disable-next-line import/no-mutable-exports
export let prestamosConciliablesCall: CancelTokenSource
export async function getPrestamosConciliables(
  queryParams: ConciliablesQueryParamsProps
): Promise<EntidadConciliableReadingDtoIPagedEnumerableProps> {
  if (typeof prestamosConciliablesCall !== typeof undefined) {
    prestamosConciliablesCall.cancel("Previous Request Cancelled")
  }
  prestamosConciliablesCall = axios.CancelToken.source()
  const {
    data,
  }: Record<"data", EntidadConciliableReadingDtoIPagedEnumerableProps> =
    await axios.get("/Prestamo/conciliables", {
      params: queryParams,
      cancelToken: prestamosConciliablesCall.token,
    })
  return {
    currentPage: data.currentPage,
    items: data.items?.map((item) => entidadConciliableReadingDtoAdapter(item)),
    totalCount: data.totalCount,
  }
}

interface PrestamosExcelQueryParamsProps {
  FechaLowerLimit?: string
  FechaUpperLimit?: string
}

export async function getPrestamosExportarExcel(
  queryParams: PrestamosExcelQueryParamsProps
) {
  return axios.get(`/prestamo/exportar-excel`, {
    params: queryParams,
    responseType: "blob",
  })
}
